/**
 * 6-char `#RRGGBB` → `#RRGGBBAA` for SVG strokes etc. Alpha is `0`–`1`.
 */
export const hexWithAlpha = (hex: string, alpha: number): string => {
  const normalized = hex.startsWith('#') ? hex : `#${hex}`
  const h = normalized.slice(1, 7)
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`hexWithAlpha: expected #RRGGBB, got ${hex}`)
  }
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
  return `#${h.toLowerCase()}${a.toString(16).padStart(2, '0')}`
}

const presets = {
  blue: {
    5: '#F4F5F8',
    10: '#E6F0FE',
    100: '#203F59',
    20: '#C8E0FD',
    30: '#A1CDFB',
    40: '#6DB8F9',
    50: '#26A7F7',
    60: '#298CCE',
    70: '#2978B0',
    80: '#286693',
    90: '#26567B',
  },
  green: {
    10: '#EBF1EC',
    100: '#284130',
    20: '#D5E1D8',
    30: '#B9CDBE',
    40: '#9DB8A4',
    50: '#80A289',
    60: '#648E71',
    70: '#427653',
    80: '#3C6A4B',
    90: '#33583F',
  },
  neutral: {
    0: '#FFFFFF',
    10: '#F3F4F4',
    100: '#121212',
    25: '#EAEAEA',
    20: '#DEDEDE',
    30: '#C8C8C8',
    40: '#B1B1B1',
    50: '#9A9A9A',
    60: '#858585',
    70: '#737373',
    80: '#616161',
    90: '#515151',
  },
  pink: {
    10: '#FDEBEE',
    100: '#6E1E39',
    20: '#FAD4DC',
    30: '#F4B9C6',
    40: '#ED9AAE',
    50: '#E37A96',
    60: '#D95A81',
    70: '#CA2265',
    80: '#B7225C',
    90: '#98224D',
  },
  red: {
    10: '#FFEAE2',
    100: '#602E20',
    20: '#FFD4C5',
    30: '#FFB79F',
    40: '#FF9575',
    50: '#FF6C47',
    55: '#B71C1C',
    60: '#DC5E3F',
    70: '#BB5237',
    80: '#9E4730',
    90: '#843C29',
  },
  turquoise: {
    10: '#ECFBF8',
    95: '#0F3744',
    100: '#062321',
    20: '#A4EBDF',
    30: '#44DAC8',
    40: '#40C4B4',
    50: '#3BAB9D',
    55: '#22AC9D',
    60: '#369388',
    70: '#317F74',
    80: '#2C6B63',
    90: '#275953',
  },
  /** Figma "shade" scale — navy text/icons on light surfaces. */
  shade: {
    700: '#2E3A59',
  },
  yellow: {
    5: '#FFFBF0',
    10: '#FFEEC5',
    100: '#483B14',
    20: '#FBDB82',
    30: '#ECC30B',
    40: '#D2AE12',
    50: '#B79816',
    60: '#9F8318',
    70: '#887018',
    80: '#745F18',
    90: '#615017',
  },
  /** Dark mode surface backgrounds (filter editor, selects, dropdowns) */
  dark: {
    surface: '#232526', // select/picklist/date input bg, dropdown bg
    surfacePanel: '#181A1B', // filter editor popover panel
  },
  /** Overlay colors for surfaces on dark/light backgrounds */
  overlay: {
    white02: '#FFFFFF05',
    white13: '#FFFFFF21',
    black03: '#00000008',
    black05: '#0000000D',
    black10: '#0000001A',
    turquoise50: '#40C4B480',
    turquoise40: '#40C4B466', // rgba(64, 196, 180, 0.40) - border on dark filter editor
    turquoise30: '#40C4B44D',
    turquoise90_40: '#27595366', // rgba(39, 89, 83, 0.40) - filter button active
    neutral10_10: '#F3F4F41A', // rgba(243, 244, 244, 0.10) - dropdown option hover
    /** presets.turquoise.100 @ 95% — floating chart tooltip panel */
    turquoise100_95: '#062321F2',
  },
  /** Temporary palette values pending final design-token placement. */
  temporary: {
    // TODO: Replace with finalized design token once design-system placement is decided.
    infoCardDarkBorder: '#206463',
  },
}

export const colors = {
  body: {
    bg: '#FFFFFF',
  },
  topbar: {
    bg: '#EAEAEA',
  },
  sidebar: {
    bg: '#061C23',
  },
  goalColors: {
    goodBlue: presets.blue['50'],
    goodTurquoise: presets.turquoise['50'],
    badPink: presets.pink['70'],
    neutralGray: presets.neutral['60'],
    _dark: {
      goodTurquoise: presets.turquoise['40'],
      badRed: presets.red['50'],
    },
  },
  checkbox: {
    bg: '#3BAB9D',
    500: '#3BAB9D',
  },
  stackedChart: {
    default: '#333333',
  },
  chart: {
    barFill: presets.turquoise['50'],
    seriesPrimary: presets.blue['50'],
    seriesSecondary: presets.blue['20'],
    /** Cartesian grid dashes: `presets.neutral.20` @ ~70% (8-digit hex). */
    gridStroke: hexWithAlpha(presets.neutral['20'], 0.7),
    /** Solid value-0 grid line: full `presets.neutral.20`. */
    gridStrokeZero: presets.neutral['20'],
    /** Inactive bar segment fill: white @ 8%. */
    barBackground: hexWithAlpha(presets.neutral['0'], 0.08),
    /** Default multi-series line / bar palette (order preserved for cycling). */
    palette: [
      presets.turquoise['30'],
      presets.blue['80'],
      presets.blue['50'],
      presets.turquoise['70'],
      presets.pink['70'],
      presets.turquoise['50'],
      presets.blue['90'],
      presets.turquoise['40'],
      presets.blue['40'],
      presets.turquoise['90'],
      presets.blue['60'],
      presets.turquoise['80'],
    ],
    /**
     * Pie / donut — Port-to-Port Transit Dashboard (Figma: carrier slices).
     * Order: dark teal → greens/mint → navy → blues → sky (avoids orange/red in donut).
     */
    piePalette: [
      presets.turquoise['70'],
      '#81C99B',
      presets.turquoise['20'],
      presets.blue['80'],
      '#4D78BC',
      presets.blue['50'],
      presets.blue['90'],
      '#E6F0FE',
      presets.turquoise['50'],
      presets.turquoise['30'],
    ],
  },
  drawerOverlay: {
    bg: '#00000033',
  },
  page: {
    dark: {
      gradientStart: '#0F3744',
      gradientEnd: '#010101',
    },
  },
  /** Figma style tokens — values inherit from `presets.*`. */
  component: {
    metricCard: {
      dark: {
        labelColor: presets.neutral['0'],
        valueColor: presets.turquoise['30'],
        borderColor: presets.overlay.turquoise50,
        background: presets.overlay.white02,
        separatorColor: presets.overlay.turquoise50,
      },
      light: {
        labelColor: presets.neutral['90'],
        valueColor: presets.turquoise['70'],
        borderColor: presets.overlay.turquoise30,
        background: presets.overlay.black03,
        separatorColor: presets.overlay.turquoise30,
      },
    },
  },
  presets,
}
